import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import type {
  InputWithUnitOption,
  InputWithUnitProps,
} from './InputWithUnit.types'
import {
  INPUT_WITH_UNIT_DEMO_ERROR_MESSAGE,
  INPUT_WITH_UNIT_DEMO_INFO_MESSAGE,
  INPUT_WITH_UNIT_DEMO_LABEL,
  INPUT_WITH_UNIT_DEMO_PLACEHOLDER,
  INPUT_WITH_UNIT_DEMO_UNITS,
  INPUT_WITH_UNIT_DEMO_VALUE,
} from './InputWithUnit.types'
import './InputWithUnit.css'

const InputWithUnit: React.FC<InputWithUnitProps> = ({
  label = INPUT_WITH_UNIT_DEMO_LABEL,
  placeholder = INPUT_WITH_UNIT_DEMO_PLACEHOLDER,
  value,
  defaultValue = '',
  unit,
  defaultUnit,
  units = INPUT_WITH_UNIT_DEMO_UNITS,
  disabled = false,
  error = false,
  helperText,
  optional = false,
  darkMode = false,
  className = '',
  previewState,
  onChange,
  onUnitChange,
}) => {
  const inputId = useId()
  const listboxId = useId()
  const fieldRef = useRef<HTMLDivElement>(null)
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [internalUnit, setInternalUnit] = useState(
    defaultUnit ?? units[0]?.value ?? '',
  )
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const isPreview = previewState != null
  const isDisabled = disabled || previewState === 'disabled'
  const isError = error || previewState === 'error'
  const isOptional = optional || previewState === 'optional'
  const isUnitOpen = menuOpen || previewState === 'unit-open'

  const resolvedValue = useMemo(() => {
    if (isPreview) {
      if (
        previewState === 'filled' ||
        previewState === 'disabled' ||
        previewState === 'error' ||
        previewState === 'with-info' ||
        previewState === 'unit-open'
      ) {
        return INPUT_WITH_UNIT_DEMO_VALUE
      }

      return ''
    }

    return value ?? internalValue
  }, [internalValue, isPreview, previewState, value])

  const resolvedUnit = useMemo(() => {
    if (isPreview) {
      return units[0]?.value ?? 'mg'
    }

    return unit ?? internalUnit
  }, [internalUnit, isPreview, unit, units])

  const resolvedHelperText = useMemo(() => {
    if (helperText != null) {
      return helperText
    }

    if (previewState === 'error') {
      return INPUT_WITH_UNIT_DEMO_ERROR_MESSAGE
    }

    if (previewState === 'with-info') {
      return INPUT_WITH_UNIT_DEMO_INFO_MESSAGE
    }

    return undefined
  }, [helperText, previewState])

  const selectedUnitLabel =
    units.find((option) => option.value === resolvedUnit)?.label ?? resolvedUnit

  const closeMenu = useCallback((): void => {
    setMenuOpen(false)
    setActiveIndex(-1)
  }, [])

  const commitValue = useCallback(
    (nextValue: string): void => {
      if (value == null) {
        setInternalValue(nextValue)
      }
      onChange?.(nextValue)
    },
    [onChange, value],
  )

  const commitUnit = useCallback(
    (nextUnit: string): void => {
      if (unit == null) {
        setInternalUnit(nextUnit)
      }
      onUnitChange?.(nextUnit)
      closeMenu()
    },
    [closeMenu, onUnitChange, unit],
  )

  const handleToggleMenu = (): void => {
    if (isPreview || isDisabled) {
      return
    }

    setMenuOpen((open) => {
      if (!open) {
        const currentIndex = units.findIndex(
          (option) => option.value === resolvedUnit,
        )
        setActiveIndex(currentIndex >= 0 ? currentIndex : 0)
      }
      return !open
    })
  }

  useEffect(() => {
    if (isPreview || !menuOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (
        fieldRef.current &&
        !fieldRef.current.contains(event.target as Node)
      ) {
        closeMenu()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [closeMenu, isPreview, menuOpen])

  const handleUnitKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ): void => {
    if (isPreview || isDisabled) {
      return
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!menuOpen) {
        setMenuOpen(true)
        const currentIndex = units.findIndex(
          (option) => option.value === resolvedUnit,
        )
        setActiveIndex(currentIndex >= 0 ? currentIndex : 0)
        return
      }

      setActiveIndex((current) => {
        if (units.length === 0) {
          return -1
        }
        if (event.key === 'ArrowDown') {
          return current < units.length - 1 ? current + 1 : 0
        }
        return current > 0 ? current - 1 : units.length - 1
      })
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (menuOpen && activeIndex >= 0 && units[activeIndex]) {
        commitUnit(units[activeIndex].value)
      } else {
        handleToggleMenu()
      }
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      closeMenu()
    }
  }

  const classNames = [
    'ds-input-with-unit',
    darkMode ? 'ds-input-with-unit--dark' : '',
    isDisabled ? 'ds-input-with-unit--disabled' : '',
    isError ? 'ds-input-with-unit--error' : '',
    isPreview ? 'ds-input-with-unit--preview' : '',
    previewState ? `ds-input-with-unit--preview-${previewState}` : '',
    resolvedValue ? 'ds-input-with-unit--filled' : '',
    isUnitOpen ? 'ds-input-with-unit--unit-open' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const fieldClassNames = [
    'ds-input-with-unit__field',
    previewState === 'focused'
      ? 'ds-input-with-unit__field--focused-preview'
      : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classNames}>
      <div className="ds-input-with-unit__label-row">
        <label className="ds-input-with-unit__label" htmlFor={inputId}>
          {label}
        </label>
        {isOptional ? (
          <span className="ds-input-with-unit__optional">(optional)</span>
        ) : null}
      </div>

      <div className={fieldClassNames} ref={fieldRef}>
        <div className="ds-input-with-unit__control">
          <input
            id={inputId}
            className="ds-input-with-unit__input"
            type="text"
            inputMode="decimal"
            value={resolvedValue}
            placeholder={placeholder}
            disabled={isDisabled || isPreview}
            aria-invalid={isError || undefined}
            aria-describedby={
              resolvedHelperText ? `${inputId}-helper` : undefined
            }
            onChange={(event) => {
              if (isPreview || isDisabled) {
                return
              }
              commitValue(event.target.value)
            }}
          />
          <span className="ds-input-with-unit__divider" aria-hidden="true" />
          <button
            type="button"
            className="ds-input-with-unit__unit-trigger"
            disabled={isDisabled || isPreview}
            aria-haspopup="listbox"
            aria-expanded={isUnitOpen}
            aria-controls={listboxId}
            onClick={handleToggleMenu}
            onKeyDown={handleUnitKeyDown}
          >
            <span className="ds-input-with-unit__unit-label">
              {selectedUnitLabel}
            </span>
            <i
              className="mgc mgc_down_small_fill ds-input-with-unit__chevron"
              aria-hidden="true"
            />
          </button>
        </div>

        {isUnitOpen ? (
          <div
            id={listboxId}
            className="ds-input-with-unit__menu"
            role="listbox"
            aria-label="Unit"
          >
            {units.map((option: InputWithUnitOption, index) => {
              const selected = option.value === resolvedUnit
              const active = index === activeIndex

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={[
                    'ds-input-with-unit__option',
                    selected ? 'ds-input-with-unit__option--selected' : '',
                    active ? 'ds-input-with-unit__option--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    if (isPreview || isDisabled) {
                      return
                    }
                    commitUnit(option.value)
                  }}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        ) : null}
      </div>

      {resolvedHelperText ? (
        <p id={`${inputId}-helper`} className="ds-input-with-unit__helper">
          {resolvedHelperText}
        </p>
      ) : null}
    </div>
  )
}

export default InputWithUnit
